import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useAppSelector } from '../../app/hooks';
import strings, { selectLanguage } from '../../components/localize/localizeSlice';
import TimeRangePickerWrapper from '../timeRangePickerWrapper/TimeRangePickerWrapper';

export interface LocationInfoFormData {
  streetNumber: string;
  city: string;
  country: string;
  zip: string;
  contactPerson: string;
  companyName: string;
  date: string;     
  startHour: [string | null, string | null]; 
  email?: string;
  mobile?: string;
  telephone?: string;
}

export interface LocationInfoFormTranslations {
  title: string;
  description: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: string;
  contactPerson: string;
  companyName: string;
  date: string;
  time: string;
  streetNumberPlaceholder: string;
  postalCodePlaceholder: string;
  cityPlaceholder: string;
  countryPlaceholder: string;
  contactPersonPlaceholder: string;
  companyNamePlaceholder: string;
  dateAndTimePlaceholder: string;
  email: string;
  phone: string;
  mobile: string;
  dateTooEarly: string;
}

export function isOptionalContentMissing(data: LocationInfoFormData | undefined): boolean {
  if (!data) return true;

  return (!data.email && !data.mobile && !data.telephone) || !data.contactPerson || !data.companyName || !data.startHour[0] || !data.startHour[1];
}

interface LocationInfoFormProps {
  onFormChange?: (isValid: boolean) => void;
  formData?: LocationInfoFormData;
  setFormData?: ( data: LocationInfoFormData) => void;
  minDate?: () => string;
  hideHours?: (hour: number) => boolean;
  hideMinutes?: (minute: number) => boolean;
  translations: LocationInfoFormTranslations;
}

const LocationInfoForm = forwardRef(({ onFormChange, formData, setFormData, minDate, hideHours, hideMinutes, translations }: LocationInfoFormProps, ref) => {
  useAppSelector(selectLanguage);
  const [errors, setErrors] = useState({
    streetNumber: false,
    city: false,
    country: false,
    zip: false,
    date: false,
    startHour: false,
  });

  const isValid = (currentErrors = errors): boolean => {
    return Object.values(currentErrors).every((error) => !error);
  };

  // Expose the validate function to the parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors = {
        streetNumber: !formData?.streetNumber,
        city: !formData?.city,
        country: !formData?.country,
        zip: !formData?.zip,
        date: !formData?.date || new Date(formData?.date) < (minDate ? new Date(minDate()) : new Date()),
        startHour: false,
      };
      setErrors(newErrors);
      return isValid(newErrors);
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (setFormData) {
      setFormData({ ...formData, [name]: value } as LocationInfoFormData);
    }
    const updatedErrors = {
      ...errors,
      [name]: !value,
    };
    setErrors(updatedErrors);
    if (onFormChange) {
      onFormChange(isValid(updatedErrors));
    }
  };

  return (
    <>
      <form className="p-4">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Street & Number */}
          <div>
            <label
              htmlFor="streetNumber"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations?.streetNumber}
            </label>
            <input
              type="text"
              id="streetNumber"
              name="streetNumber"
              value={formData?.streetNumber || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${
                errors.streetNumber ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={translations?.streetNumberPlaceholder}
            />
            {errors.streetNumber && (
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: translations.streetNumber})}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.city}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData?.city || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${
                errors.city ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={translations.cityPlaceholder}
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: translations.city})}</p>
            )}
          </div>

          {/* Country */}
          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.country}
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData?.country || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${
                errors.country ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={translations.countryPlaceholder}
            />
            {errors.country && (
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: translations.country})}</p>
            )}
          </div>

          {/* Zip */}
          <div>
            <label
              htmlFor="zip"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
               {translations.postalCode}
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              value={formData?.zip || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${
                errors.zip ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={translations.postalCodePlaceholder}
            />
            {errors.zip && (
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: translations.postalCode})}</p>
            )}
          </div>

          {/* Date */}
          <div>
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.date}
              </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData?.date || ""}
              min={minDate ? minDate() : undefined}
              onChange={handleChange}
              className={`mt-1 block w-full  rounded-md border ${
                errors.date ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              style={{ WebkitAppearance: "none" }}
            />
            {errors.date && formData?.date == null &&(
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: translations.date})}</p>
            )}
            {errors.date &&(
                <p className="mt-1 text-sm text-red-500">
                {strings.formatString(
                  translations.dateTooEarly,
                  {
                  minDate: minDate
                    ? new Date(minDate()).toLocaleDateString()
                    : ""
                  }
                )}
                </p>
            )}
          </div>

          {/* Start Hour */}
          <div>
            <label
              htmlFor="startHour"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {strings.form.pickupLocation.time} 
            </label>
            <TimeRangePickerWrapper
              hideHours={hideHours}
              hideMinutes={hideMinutes}
              errors={errors}
              value={formData?.startHour || [null, null]}
              onChange={(startTime, endTime) => {
                if (setFormData) {
                  setFormData({ ...formData, startHour: [startTime, endTime] } as LocationInfoFormData);
                }
                const updatedErrors = {
                  ...errors,
                  startHour: !startTime || !endTime,
                };
                setErrors(updatedErrors);
                if (onFormChange) {
                  onFormChange(isValid(updatedErrors));
                }
              }}
            />

            {errors.startHour && (
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, {ValueName: translations.time})}</p>
            )}
          </div>

          {/* Contact Person */}
          <div>
            <label
              htmlFor="contactPerson"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.contactPerson}
            </label>
            <input
              type="text"
              id="contactPerson"
              name="contactPerson"
              value={formData?.contactPerson || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border 
               shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={translations.contactPersonPlaceholder}
            />
            
          </div>

          {/* Company Name */}
          <div>
            <label
              htmlFor="companyName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.companyName}
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData?.companyName || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={translations.companyNamePlaceholder}
            />
           
          </div>

           {/* Email (optional) */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.email || "E-Mail"}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData?.email || ""}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
              placeholder={"person@example.com"}
            />
          </div>

          {/* Mobile (optional) */}
          <div>
            <label
              htmlFor="mobile"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.mobile || "Mobil"}
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData?.mobile || ""}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
              placeholder={ "+49123456789" }
            />
          </div>

          {/* Telephone (optional) */}
          <div>
            <label
              htmlFor="telephone"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {translations.phone || "Telefon"}
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData?.telephone || ""}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
              placeholder={"+49123456789"}
            />
          </div>
        </div>
      </form>
    </>
  );
});

export default LocationInfoForm;